import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { useBranding } from '../lib/branding.tsx';
import { companyInfoLabel, stationOptions } from '../lib/companyInfo.ts';
import type { Company } from '../lib/types.ts';

export function AdminCompanies() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => adminApi.get<{ companies: Company[] }>('/api/companies'),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const companies = useMemo(() => {
    const list = data?.companies ?? [];
    if (!filter.trim()) return list;
    const q = filter.toLowerCase();
    return list.filter(
      (c) =>
        c.company_name.toLowerCase().includes(q) ||
        c.contact_name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q),
    );
  }, [data, filter]);

  const inviteMutation = useMutation({
    mutationFn: (id: string) => adminApi.post<{ sent: boolean; expires_at: string }>(`/api/companies/${id}/invite`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });

  if (isLoading) return <CenteredSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Companies</h1>
          <p className="mt-1 text-sm text-slate-500">
            {companies.length} of {data?.companies.length ?? 0}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TextInput
            placeholder="Search…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-64"
          />
          <Btn onClick={() => setCreateOpen(true)}>+ New company</Btn>
        </div>
      </div>

      {companies.length === 0 ? (
        <EmptyState
          title="No companies yet"
          description="Add a company to invite them to complete the questionnaire."
          action={<Btn onClick={() => setCreateOpen(true)}>+ New company</Btn>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Company</th>
                <th className="px-4 py-2">Contact</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Progress</th>
                <th className="px-4 py-2">Expires</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {companies.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/admin/companies/${c.id}`} className="font-medium text-slate-900 hover:underline">
                      {c.company_name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {c.contact_name}
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </td>
                  <td className="px-4 py-2"><CompanyStatusBadge status={c.status} /></td>
                  <td className="px-4 py-2 text-slate-700">{c.progress}%</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Btn
                      size="sm"
                      variant="secondary"
                      loading={inviteMutation.isPending && inviteMutation.variables === c.id}
                      onClick={() => inviteMutation.mutate(c.id)}
                    >
                      {c.invited_at ? 'Resend invite' : 'Send invite'}
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inviteMutation.error && <ErrorMessage error={inviteMutation.error} />}
      {inviteMutation.isSuccess && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Invitation sent.
        </div>
      )}

      <CreateCompanyModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

type CreateForm = {
  company_name: string;
  contact_name: string;
  email: string;
  contractor_type: string;
  vendor_number: string;
  contract_number: string;
  cluster_region: string;
  station: string;
};

const EMPTY_CREATE_FORM: CreateForm = {
  company_name: '',
  contact_name: '',
  email: '',
  contractor_type: '',
  vendor_number: '',
  contract_number: '',
  cluster_region: '',
  station: '',
};

function CreateCompanyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { branding } = useBranding();
  const [form, setForm] = useState<CreateForm>(EMPTY_CREATE_FORM);
  const [inviteAfter, setInviteAfter] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const stations = stationOptions(branding);

  function field<K extends keyof CreateForm>(key: K, value: CreateForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
      };
      for (const key of [
        'contractor_type',
        'vendor_number',
        'contract_number',
        'cluster_region',
        'station',
      ] as const) {
        const trimmed = form[key].trim();
        if (trimmed.length > 0) payload[key] = trimmed;
      }
      const { company } = await adminApi.post<{ company: Company }>('/api/companies', payload);
      if (inviteAfter) {
        await adminApi.post(`/api/companies/${company.id}/invite`).catch((e) => {
          throw e instanceof ApiError ? e : new Error('Created, but invite failed');
        });
      }
      await qc.invalidateQueries({ queryKey: ['companies'] });
      setForm(EMPTY_CREATE_FORM);
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New company"
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} loading={submitting} disabled={!form.company_name || !form.email}>
            Create{inviteAfter ? ' & invite' : ''}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Company legal name" required>
            <TextInput
              value={form.company_name}
              onChange={(e) => field('company_name', e.target.value)}
            />
          </Field>
          <Field label="Contact name" required>
            <TextInput
              value={form.contact_name}
              onChange={(e) => field('contact_name', e.target.value)}
            />
          </Field>
          <Field label="Contact email" required>
            <TextInput
              type="email"
              value={form.email}
              onChange={(e) => field('email', e.target.value)}
            />
          </Field>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 text-sm font-semibold text-slate-700">
            Pre-filled company information
          </div>
          <p className="mb-3 text-xs text-slate-500">
            These values appear pre-filled on the company&apos;s questionnaire when they
            accept the invite. All optional — leave blank if not applicable.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={companyInfoLabel(branding, 'contractorType')}>
              <TextInput
                value={form.contractor_type}
                onChange={(e) => field('contractor_type', e.target.value)}
              />
            </Field>
            <Field label={companyInfoLabel(branding, 'vendorNumber')}>
              <TextInput
                value={form.vendor_number}
                onChange={(e) => field('vendor_number', e.target.value)}
              />
            </Field>
            <Field label={companyInfoLabel(branding, 'contractNumber')}>
              <TextInput
                value={form.contract_number}
                onChange={(e) => field('contract_number', e.target.value)}
              />
            </Field>
            <Field label={companyInfoLabel(branding, 'clusterRegion')}>
              <TextInput
                value={form.cluster_region}
                onChange={(e) => field('cluster_region', e.target.value)}
              />
            </Field>
            <Field
              label={companyInfoLabel(branding, 'station')}
              hint={
                stations.length === 0
                  ? 'Add stations in Settings to populate this dropdown.'
                  : undefined
              }
            >
              <Select
                value={form.station}
                onChange={(e) => field('station', e.target.value)}
                disabled={stations.length === 0}
              >
                <option value="">— Select —</option>
                {stations.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
            </Field>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={inviteAfter} onChange={(e) => setInviteAfter(e.target.checked)} />
          Send invitation immediately
        </label>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
